import { ReactNode, useMemo } from 'react';

export type LayoutV2ButtonHeight = '40' | '48';
export type LayoutV2ButtonVariant = 'purple' | 'outline' | 'ghost';

export interface LayoutV2ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  id: string;
  height?: LayoutV2ButtonHeight;
  variant?: LayoutV2ButtonVariant;
  className?: string;
  children: ReactNode;
}

const BUTTON_HEIGHT_CLASSNAME: Record<LayoutV2ButtonHeight, string> = {
  '40': 'h-10',
  '48': 'h-12',
};

const BUTTON_VARIANT_CLASSNAME: Record<LayoutV2ButtonVariant, string> = {
  purple: 'text-neutral-000 bg-purple-400 hover:bg-purple-500',
  outline:
    'text-neutral-600 hover:text-neutral-700 bg-neutral-000 hover:bg-neutral-200 border border-neutral-300',
  ghost:
    'text-neutral-600 hover:text-neutral-700 bg-neutral-000 hover:bg-neutral-200',
};

const BUTTON_BASE_CLASSNAME =
  'flex flex-row gap-x-3 px-4 rounded-full items-center justify-center transition-all group';

const LayoutV2Button: React.FunctionComponent<LayoutV2ButtonProps> = function ({
  id,
  height = '48',
  variant = 'purple',
  className,
  children,
  type,
  ...rest
}) {
  const heightClassName = useMemo(
    () => BUTTON_HEIGHT_CLASSNAME[height],
    [height],
  );
  const variantBaseClassName = useMemo(
    () => BUTTON_VARIANT_CLASSNAME[variant],
    [variant],
  );
  const buttonClassName = useMemo(
    () =>
      [BUTTON_BASE_CLASSNAME, heightClassName, variantBaseClassName, className]
        .filter(Boolean)
        .join(' '),
    [heightClassName, variantBaseClassName, className],
  );
  const buttonType: React.ButtonHTMLAttributes<HTMLButtonElement>['type'] =
    useMemo(() => {
      if (!type) return 'button';
      return type;
    }, [type]);
  return (
    // eslint-disable-next-line
    <button id={id} type={buttonType} className={buttonClassName} {...rest}>
      {children}
    </button>
  );
};

export default LayoutV2Button;
